<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import type { PermissionSubject } from '@/features/FormBuilder';
  import { Checkbox, ColoredButton, Input } from '@/shared/ui';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import FieldRef from './FieldRef.vue';

  const lab = useMatrixFillLabContext();

  const SELECT_CLASS =
    'w-full rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] px-2 py-1.5 text-xs text-[var(--color-gray-07)]';

  const fieldId = ref('');
  const positionIds = ref<number[]>([]);
  const activityIds = ref<number[]>([]);
  const positionFilter = ref('');
  const activityFilter = ref('');

  const canAdd = computed(() => fieldId.value !== '' && (positionIds.value.length > 0 || activityIds.value.length > 0));

  function filterBy(list: PermissionSubject[], term: string): PermissionSubject[] {
    const needle = term.trim().toLowerCase();
    return needle ? list.filter((item) => item.name.toLowerCase().includes(needle)) : list;
  }

  const positions = computed(() => filterBy(lab.subjects.positions.value, positionFilter.value));
  const activities = computed(() => filterBy(lab.subjects.functionalActivities.value, activityFilter.value));

  watch(() => lab.matrix.value?.id, () => {
    fieldId.value = '';
    positionIds.value = [];
    activityIds.value = [];
  });

  function toggle(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
  }

  function names(ids: number[], source: PermissionSubject[]): string {
    const found = source.filter((item) => ids.includes(item.id)).map((item) => item.name);
    return found.length > 0 ? found.join(', ') : 'nenhum';
  }

  function add(): void {
    if (!canAdd.value) return;
    lab.setGrant(fieldId.value, [...positionIds.value], [...activityIds.value]);
    fieldId.value = '';
    positionIds.value = [];
    activityIds.value = [];
  }
</script>

<template>
  <div
    class="flex flex-col gap-2"
    data-testid="grant-editor"
  >
    <label class="flex flex-col gap-1">
      <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Campo</span>
      <select
        v-model="fieldId"
        :class="SELECT_CLASS"
        data-testid="grant-field"
      >
        <option value="">
          Escolha o campo
        </option>
        <option
          v-for="field in lab.fieldOptions.value"
          :key="field.id"
          :value="field.id"
        >
          {{ field.label }} ({{ field.typeLabel }})
        </option>
      </select>
    </label>
    <div class="grid grid-cols-2 gap-2">
      <div class="flex min-w-0 flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Cargos ({{ positionIds.length }})</span>
        <Input
          v-model="positionFilter"
          placeholder="Filtrar"
          class="h-7 bg-white text-xs md:text-xs"
        />
        <div class="max-h-32 overflow-auto rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] p-1.5">
          <label
            v-for="position in positions"
            :key="position.id"
            class="flex cursor-pointer items-center gap-2 py-0.5"
          >
            <Checkbox
              :id="`grant-position-${position.id}`"
              :checked="positionIds.includes(position.id)"
              @toggle="positionIds = toggle(positionIds, position.id)"
            />
            <span class="truncate">{{ position.name }}</span>
          </label>
        </div>
      </div>
      <div class="flex min-w-0 flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Atividades ({{ activityIds.length }})</span>
        <Input
          v-model="activityFilter"
          placeholder="Filtrar"
          class="h-7 bg-white text-xs md:text-xs"
        />
        <div class="max-h-32 overflow-auto rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] p-1.5">
          <label
            v-for="activity in activities"
            :key="activity.id"
            class="flex cursor-pointer items-center gap-2 py-0.5"
          >
            <Checkbox
              :id="`grant-activity-${activity.id}`"
              :checked="activityIds.includes(activity.id)"
              @toggle="activityIds = toggle(activityIds, activity.id)"
            />
            <span class="truncate">{{ activity.name }}</span>
          </label>
        </div>
      </div>
    </div>
    <ColoredButton
      text="Aplicar permissão"
      :disabled="!canAdd"
      class="self-start"
      data-testid="grant-add"
      @click="add"
    />

    <ul
      v-if="lab.labGrants.value.length > 0"
      class="flex flex-col gap-1.5"
      data-testid="grant-editor-list"
    >
      <li
        v-for="grant in lab.labGrants.value"
        :key="grant.fieldId"
        class="rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] p-2"
      >
        <div class="flex items-center gap-2">
          <FieldRef :id="grant.fieldId" />
          <span class="text-[10px] text-[var(--color-gray-05)]">(simulada)</span>
          <button
            type="button"
            class="ml-auto text-[10px] text-red-600 underline"
            @click="lab.removeGrant(grant.fieldId)"
          >
            remover
          </button>
        </div>
        <p class="mt-1">
          Cargos: {{ names(grant.positionIds, lab.subjects.positions.value) }}
        </p>
        <p>Atividades: {{ names(grant.functionalActivityIds, lab.subjects.functionalActivities.value) }}</p>
      </li>
    </ul>
  </div>
</template>
